/* eslint-disable react/display-name */
import React, { forwardRef, Ref, useState } from 'react';
import { Select as AntSelect, SelectProps } from 'antd';
import './Select.scss';
import { twJoin, twMerge } from '@shared/utils/twMerge';
import { BaseSelectRef } from 'rc-select';
import { SelectArrow } from '@shared/ui/icons';

export type CustomSelectProps = SelectProps & {
  label?: string;
  dark?: boolean;
  classNameLabel?: string;
  classNameError?: string;
  name?: string;
  required?: boolean;
  asText?: boolean;
  ['data-testid']?: string;
  errors?: string;
  inOneLine?: boolean;
  classNameInputWrapper?: string;
  closeOnSelect?: boolean;
  child?: React.ReactNode;
  autoDropdownWidth?: boolean;
};

export const Select = forwardRef<HTMLSelectElement, CustomSelectProps>(
  (
    {
      asText,
      dark,
      inOneLine,
      classNameLabel,
      classNameInputWrapper,
      closeOnSelect,
      autoDropdownWidth,
      ...props
    }: CustomSelectProps,
    ref
  ) => {
    const styleInOneLineWrapper = inOneLine ? 'flex flex-row justify-between' : '';
    const styleInOneLineLabel = inOneLine ? 'pt-[1.2rem]' : '';

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const handleDropdownToggle = (open: boolean) => {
      setIsDropdownOpen(open);
    };

    return (
      <div
        data-testid={`Select-${props['data-testid']}`}
        className={twMerge('D7Select', dark && 'dark')}
      >
        <label className={twMerge('w-full', styleInOneLineWrapper, classNameLabel)}>
          {props.label && (
            <div className={twMerge('mb-1', styleInOneLineLabel)}>
              <span
                className={twMerge(
                  'text-1424 font-semibold',
                  props.required && !asText && 'field-required',
                  asText && 'text-1624 text-dimGray font-normal'
                )}
              >
                {props.label}
              </span>
            </div>
          )}
          <div className={twMerge('max-w-[41.2rem] grow', classNameInputWrapper)}>
            <AntSelect
              open={isDropdownOpen}
              {...props}
              disabled={asText || props.disabled}
              tabIndex={asText ? -1 : 0}
              onClick={e => {
                props?.onClick?.(e);
              }}
              suffixIcon={
                !asText
                  ? props.suffixIcon || (
                      <SelectArrow
                        width={16}
                        height={16}
                        className={twMerge(
                          'transition-transform',
                          dark && '!text-pastelBlue',
                          isDropdownOpen ? 'rotate-180' : 'rotate-0'
                        )}
                      />
                    )
                  : null
              }
              onChange={(value, option) => {
                props?.onChange?.(value, option);
                if (closeOnSelect) {
                  setIsDropdownOpen(false);
                }
              }}
              onDropdownVisibleChange={props.onDropdownVisibleChange ?? handleDropdownToggle}
              ref={ref as unknown as Ref<BaseSelectRef>}
              className={twMerge(
                'w-full max-w-[41.2rem]',
                props.mode === 'multiple' || props.mode === 'tags'
                  ? 'min-h-[4.8rem]'
                  : 'h-[4.8rem]',
                props.errors && 'ant-select-error',
                props.className,
                asText && 'select-as-text'
              )}
              value={props.value === '' ? undefined : props.value}
              rootClassName={twJoin(props.rootClassName, dark && 'D7Dark')}
              dropdownMatchSelectWidth={autoDropdownWidth ? false : undefined}
              popupClassName={twJoin(
                props.popupClassName,
                autoDropdownWidth && 'auto-dropdown-width'
              )}
            />
            {props.errors && (
              <span
                data-testid={`${props.name}-error-container`}
                className={twMerge(
                  'ant-select-error text-1420 font-normal',
                  props.errors ? 'visible' : 'invisible',
                  props.classNameError
                )}
              >
                {props.errors}
              </span>
            )}
            {props.child}
          </div>
        </label>
      </div>
    );
  }
);
